import React, { useState } from 'react';
import {
    Callout,
    DefaultButton,
    DirectionalHint,
    Dropdown,
    IDropdownOption,
    ITheme,
    IconButton,
    SearchBox,
    SpinButton,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import {
    SreEntityType,
    SreInflightSearchQuery,
    SreStageCatalogEntry,
    SRE_INFLIGHT_STAGE_STATES,
    SRE_STAGE_STATE_NAMES,
    SreStageState,
} from '../../../../../AGSS/models/SreHealth';

interface ISreTriageFiltersProps {
    type: SreEntityType;
    query: SreInflightSearchQuery;
    stageCatalog: SreStageCatalogEntry[];
    orchestrationTypes: string[];
    theme: ITheme;
    onChange: (updates: Partial<SreInflightSearchQuery>) => void;
    onClearAll: () => void;
}

const ALL_STAGE_STATES = [
    SreStageState.New,
    SreStageState.InProgress,
    SreStageState.Queued,
    SreStageState.Complete,
    SreStageState.Cancelled,
    SreStageState.Failed,
];

const countActiveFilters = (query: SreInflightSearchQuery): number => {
    let count = 0;
    if (query.uniqueIdentifier) count++;
    if (query.stage?.length) count++;
    if (query.orchestrationType) count++;
    if (query.pipeline) count++;
    if (query.region) count++;
    if (query.agedOlderThanHours) count++;
    if (query.minRetryCount) count++;
    if (
        query.stageState?.length &&
        // Only counts as a filter when it differs from the default in-flight set.
        (query.stageState.length !== SRE_INFLIGHT_STAGE_STATES.length ||
            !SRE_INFLIGHT_STAGE_STATES.every((s) =>
                query.stageState!.includes(s)
            ))
    ) {
        count++;
    }
    return count;
};

export const hasActiveFilters = (query: SreInflightSearchQuery): boolean =>
    countActiveFilters(query) > 0 || Boolean(query.search);

export const SreTriageFilters: React.FC<ISreTriageFiltersProps> = ({
    type,
    query,
    stageCatalog,
    orchestrationTypes,
    theme,
    onChange,
    onClearAll,
}) => {
    const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
    const filterButtonId = 'sre-triage-filter-button';
    const activeCount = countActiveFilters(query);

    // Stage number first, matching how the list renders a stage. The plan position moves to the
    // tooltip: showing both inline read as list numbering, so picking "stage 12" meant clicking the
    // twelfth entry rather than the one actually numbered 12.
    const stageOptions: IDropdownOption[] = stageCatalog.map((entry) => ({
        key: entry.number,
        text: `${entry.number} · ${entry.name}`,
        title: `${entry.name} — stage ${entry.number}, step ${entry.order} of ${stageCatalog.length}`,
    }));

    const stateOptions: IDropdownOption[] = ALL_STAGE_STATES.map((state) => ({
        key: state,
        text: SRE_STAGE_STATE_NAMES[state],
    }));

    const typeOptions: IDropdownOption[] = [
        { key: '', text: 'All types' },
        ...Array.from(
            new Set(
                [...orchestrationTypes, query.orchestrationType].filter(
                    (name): name is string => Boolean(name)
                )
            )
        ).map((name) => ({ key: name, text: name })),
    ];

    return (
        <Stack
            horizontal
            wrap
            verticalAlign="center"
            tokens={{ childrenGap: 8 }}
        >
            <SearchBox
                ariaLabel="Filter by unique identifier"
                placeholder="Filter by unique identifier"
                value={query.search ?? ''}
                onChange={(_, value) => onChange({ search: value, page: 1 })}
                onClear={() => onChange({ search: undefined, page: 1 })}
                styles={{ root: { width: 300, maxWidth: '100%' } }}
            />

            <DefaultButton
                id={filterButtonId}
                text={activeCount > 0 ? `Filters (${activeCount})` : 'Filters'}
                iconProps={{ iconName: 'Filter' }}
                onClick={() => setIsFlyoutOpen((open) => !open)}
                styles={{
                    root:
                        activeCount > 0
                            ? {
                                  borderColor: theme.palette.themePrimary,
                                  color: theme.palette.themePrimary,
                              }
                            : undefined,
                }}
            />

            {(activeCount > 0 || query.search) && (
                <DefaultButton
                    text="Clear"
                    iconProps={{ iconName: 'ClearFilter' }}
                    onClick={onClearAll}
                />
            )}

            {isFlyoutOpen && (
                <Callout
                    target={`#${filterButtonId}`}
                    onDismiss={() => setIsFlyoutOpen(false)}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    setInitialFocus
                >
                    <Stack
                        tokens={{ childrenGap: 12 }}
                        styles={{
                            root: {
                                padding: 16,
                                width: 340,
                                maxWidth: 'calc(100vw - 32px)',
                                boxSizing: 'border-box',
                            },
                        }}
                    >
                        <Stack
                            horizontal
                            horizontalAlign="space-between"
                            verticalAlign="center"
                        >
                            <Text
                                variant="mediumPlus"
                                styles={{ root: { fontWeight: 600 } }}
                            >
                                Filters
                            </Text>
                            <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                ariaLabel="Close filters"
                                onClick={() => setIsFlyoutOpen(false)}
                            />
                        </Stack>

                        <TextField
                            label="Exact unique identifier"
                            value={query.uniqueIdentifier ?? ''}
                            placeholder="account/project/buildnumber"
                            onChange={(_, value) =>
                                onChange({
                                    uniqueIdentifier: value || undefined,
                                    page: 1,
                                })
                            }
                        />

                        <Dropdown
                            label="Stage"
                            multiSelect
                            disabled={stageOptions.length === 0}
                            placeholder="All stages"
                            options={stageOptions}
                            selectedKeys={query.stage ?? []}
                            onChange={(_, option) => {
                                if (!option) return;
                                const current = query.stage ?? [];
                                const next = option.selected
                                    ? [...current, option.key as number]
                                    : current.filter((s) => s !== option.key);
                                onChange({
                                    stage: next.length ? next : undefined,
                                    page: 1,
                                });
                            }}
                        />

                        <Dropdown
                            label="Stage state"
                            multiSelect
                            options={stateOptions}
                            selectedKeys={
                                query.stageState ?? SRE_INFLIGHT_STAGE_STATES
                            }
                            onChange={(_, option) => {
                                if (!option) return;
                                const current =
                                    query.stageState ??
                                    SRE_INFLIGHT_STAGE_STATES;
                                const next = option.selected
                                    ? [...current, option.key as number]
                                    : current.filter((s) => s !== option.key);
                                onChange({
                                    stageState: next.length ? next : undefined,
                                    page: 1,
                                });
                            }}
                        />

                        {typeOptions.length > 1 && (
                            <Dropdown
                                label="Orchestration type"
                                placeholder="All types"
                                options={typeOptions}
                                selectedKey={query.orchestrationType ?? ''}
                                onChange={(_, option) =>
                                    onChange({
                                        orchestrationType:
                                            (option?.key as string) ||
                                            undefined,
                                        page: 1,
                                    })
                                }
                            />
                        )}

                        {type === 'build' && (
                            <>
                                <TextField
                                    label="Pipeline"
                                    value={query.pipeline ?? ''}
                                    onChange={(_, value) =>
                                        onChange({
                                            pipeline: value || undefined,
                                            page: 1,
                                        })
                                    }
                                />
                                <TextField
                                    label="Region"
                                    value={query.region ?? ''}
                                    onChange={(_, value) =>
                                        onChange({
                                            region: value || undefined,
                                            page: 1,
                                        })
                                    }
                                />
                            </>
                        )}

                        <SpinButton
                            label="Older than (hours)"
                            min={0}
                            step={1}
                            value={String(query.agedOlderThanHours ?? 0)}
                            onChange={(_, value) => {
                                const parsed = Number(value);
                                onChange({
                                    agedOlderThanHours:
                                        Number.isFinite(parsed) && parsed > 0
                                            ? Math.min(
                                                  876000,
                                                  Math.floor(parsed)
                                              )
                                            : undefined,
                                    page: 1,
                                });
                            }}
                        />

                        <SpinButton
                            label="Minimum retries"
                            min={0}
                            step={1}
                            value={String(query.minRetryCount ?? 0)}
                            onChange={(_, value) => {
                                const parsed = Number(value);
                                onChange({
                                    minRetryCount:
                                        Number.isFinite(parsed) && parsed > 0
                                            ? Math.min(
                                                  2147483647,
                                                  Math.floor(parsed)
                                              )
                                            : undefined,
                                    page: 1,
                                });
                            }}
                        />

                        <Stack
                            horizontal
                            horizontalAlign="end"
                            tokens={{ childrenGap: 8 }}
                        >
                            <DefaultButton
                                text="Clear all"
                                onClick={() => {
                                    onClearAll();
                                    setIsFlyoutOpen(false);
                                }}
                            />
                            <DefaultButton
                                text="Done"
                                primary
                                onClick={() => setIsFlyoutOpen(false)}
                            />
                        </Stack>
                    </Stack>
                </Callout>
            )}
        </Stack>
    );
};
